import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getOrdersForAdmin, getOrderAnalyticsAndFilters } from '../services/adminOrderService';
import TimeRangeSelector from '../components/TimeRangeSelector';
import OrderDetailModal from '../components/OrderDetailModal';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { getStatusLabel } from '../utils/statusLabels';
import { ORDERS_TABLE_ID } from '../lib/appwrite';
import './OrdersPage.css';

const formatCurrency = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-TZ', { style: 'decimal' });
};

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { dateStyle: 'short' });
};

const OrdersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [filterData, setFilterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStore, setFilterStore] = useState('');
  const [filterCustomer, setFilterCustomer] = useState(() => location.state?.filterCustomer || '');
  const [filterDriver, setFilterDriver] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  const [timeRange, setTimeRange] = useState('month');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [ordersRes, filtersRes] = await Promise.all([
        getOrdersForAdmin({
          storeId: filterStore || undefined,
          customerId: filterCustomer || undefined,
          driverId: filterDriver || undefined,
          timeRange,
        }),
        getOrderAnalyticsAndFilters({ timeRange }),
      ]);
      setOrders(ordersRes || []);
      setFilterData(filtersRes);
    } catch (err) {
      const msg = err.message || t('orders.loadFailed');
      const isAuthError =
        typeof msg === 'string' &&
        (msg.toLowerCase().includes('not authorized') || msg.toLowerCase().includes('unauthorized'));
      setError(
        isAuthError
          ? `${msg} Add REACT_APP_APPWRITE_API_KEY to .env (see .env.example) to use server-level access, or update table permissions in Appwrite Console.`
          : msg
      );
      setOrders([]);
      setFilterData(null);
    } finally {
      setLoading(false);
    }
  }, [filterStore, filterCustomer, filterDriver, timeRange, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData, { realtimeTables: [ORDERS_TABLE_ID] });

  const filteredOrders = orders.filter((o) => {
    const matchSearch =
      !search ||
      (o.orderId || '').toLowerCase().includes(search.toLowerCase()) ||
      (o.storeName || '').toLowerCase().includes(search.toLowerCase()) ||
      (filterData?.customerMap?.[o.customerId] || '').toLowerCase().includes(search.toLowerCase());
    return matchSearch;
  });

  const chartColors = ['#039855', '#10b981', '#34d399', '#f79009', '#64748b', '#6366f1'];

  if (loading && !filterData) {
    return (
      <div className="orders-loading">
        <div className="dashboard-spinner" />
        <p>{t('orders.loading')}</p>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-header">
        <div>
          <h1 className="dashboard-title">{t('nav.orders')}</h1>
          <p className="dashboard-subtitle">
            {t('orders.subtitle')}
          </p>
        </div>
        <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
      </div>

      {error && (
        <div className="orders-error" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <nav className="orders-tabs">
        <button
          type="button"
          className={`orders-tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          {t('orders.orderList')}
        </button>
        <button
          type="button"
          className={`orders-tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          {t('nav.analytics')}
        </button>
      </nav>

      {activeTab === 'list' && (
        <>
          <div className="orders-toolbar">
            <div className="orders-search-wrap">
              <img src="/icons/Search.svg" alt="" className="orders-search-icon" aria-hidden />
              <input
                type="search"
                placeholder={t('orders.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="orders-search"
              />
            </div>
            <select
              value={filterStore}
              onChange={(e) => setFilterStore(e.target.value)}
              className="orders-filter"
            >
              <option value="">{t('orders.allStores')}</option>
              {(filterData?.filters?.stores || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <select
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
              className="orders-filter"
            >
              <option value="">{t('orders.allCustomers')}</option>
              {(filterData?.filters?.customers || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              value={filterDriver}
              onChange={(e) => setFilterDriver(e.target.value)}
              className="orders-filter"
            >
              <option value="">{t('orders.allDrivers')}</option>
              {(filterData?.filters?.drivers || []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            <button type="button" className="orders-btn-refresh" onClick={loadData}>
              ⟳ {t('common.refresh')}
            </button>
          </div>

          <div className="orders-table-wrap">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>{t('orders.orderId')}</th>
                  <th>{t('orders.date')}</th>
                  <th>{t('orders.store')}</th>
                  <th>{t('orders.customer')}</th>
                  <th>{t('orders.driver')}</th>
                  <th>{t('orders.status')}</th>
                  <th>{t('orders.items')}</th>
                  <th>{t('orders.totalTzs')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="orders-empty">
                      {t('orders.noneFound')}
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((o) => (
                    <tr
                      key={o.orderId}
                      className="orders-row-clickable"
                      onClick={() => setSelectedOrder(o)}
                    >
                      <td>
                        <strong>{o.orderId}</strong>
                      </td>
                      <td>{formatDate(o.orderDate)}</td>
                      <td>
                        <button
                          type="button"
                          className="orders-link"
                          onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/stores/${o.storeId}`); }}
                        >
                          {o.storeName || o.storeId || '—'}
                        </button>
                      </td>
                      <td>{filterData?.customerMap?.[o.customerId] || o.customerId || '—'}</td>
                      <td>{o.driver?.driverName || o.driver?.driverId || '—'}</td>
                      <td>
                        <span className={`orders-status orders-status-${(o.status || '').toLowerCase()}`}>
                          {o.status ? getStatusLabel(t, o.status) : '—'}
                        </span>
                      </td>
                      <td>{o.items?.length ?? 0}</td>
                      <td><strong>{formatCurrency(o.totalAmount)}</strong></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'analytics' && filterData && (
        <div className="orders-analytics">
          <p className="orders-analytics-desc">{t('orders.analyticsDesc')}</p>
          <div className="orders-analytics-cards">
            <div className="orders-analytics-card">
              <span className="orders-analytics-value">{filterData.analytics.totalOrders}</span>
              <span className="orders-analytics-label">{t('dashboard.totalOrders')}</span>
            </div>
            <div className="orders-analytics-card orders-analytics-card-revenue">
              <span className="orders-analytics-value">{formatCurrency(filterData.analytics.totalRevenue)}</span>
              <span className="orders-analytics-label">{t('orders.totalRevenueTzs')}</span>
            </div>
            <div className="orders-analytics-card">
              <span className="orders-analytics-value">
                {filterData.analytics.totalOrders > 0
                  ? formatCurrency(filterData.analytics.totalRevenue / filterData.analytics.totalOrders)
                  : '—'}
              </span>
              <span className="orders-analytics-label">{t('orders.avgOrderValue')}</span>
            </div>
          </div>

          <div className="orders-analytics-charts">
            <div className="orders-analytics-chart-box">
              <h3 className="orders-analytics-chart-title">{t('orders.ordersRevenue12Months')}</h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={filterData.analytics.byMonth.map((m) => ({ name: m.label, orders: m.orders, revenue: m.revenue }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#6b7280" />
                  <YAxis yAxisId="left" stroke="#6b7280" />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} stroke="#6b7280" />
                  <Tooltip formatter={(v, name) => [name === 'revenue' ? formatCurrency(v) : v, name]} />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="orders" name={t('nav.orders')} stroke="#039855" fill="#10b981" fillOpacity={0.4} strokeWidth={2} />
                  <Area yAxisId="right" type="monotone" dataKey="revenue" name={t('orders.revenueTzs')} stroke="#f79009" fill="#f79009" fillOpacity={0.3} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="orders-analytics-chart-box">
              <h3 className="orders-analytics-chart-title">{t('orders.ordersByStatus')}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={filterData.analytics.byStatus.map((s) => ({ name: s.status ? getStatusLabel(t, s.status) : t('orders.unknown'), value: s.count }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {filterData.analytics.byStatus.map((_, i) => (
                      <Cell key={i} fill={chartColors[i % chartColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <h3 className="orders-analytics-section-title">{t('orders.monthlyBreakdown')}</h3>
          <div className="orders-analytics-chart-box">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={filterData.analytics.byMonth.map((m) => ({ name: m.label, orders: m.orders, revenue: m.revenue }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#6b7280" />
                <YAxis yAxisId="left" stroke="#6b7280" />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} stroke="#6b7280" />
                <Tooltip formatter={(v, name) => [name === 'revenue' ? formatCurrency(v) : v, name]} />
                <Legend />
                <Bar yAxisId="left" dataKey="orders" name={t('nav.orders')} fill="#039855" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="revenue" name={t('orders.revenueTzs')} fill="#f79009" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <button type="button" className="orders-analytics-refresh" onClick={loadData}>
            ⟳ {t('orders.refreshAnalytics')}
          </button>
        </div>
      )}

      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          customerName={filterData?.customerMap?.[selectedOrder.customerId]}
          customerEmail={filterData?.customerDetailMap?.[selectedOrder.customerId]?.email}
          customerPhone={filterData?.customerDetailMap?.[selectedOrder.customerId]?.phone}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          onClose={() => setSelectedOrder(null)}
          onNavigateStore={(storeId) => { setSelectedOrder(null); navigate(`/dashboard/stores/${storeId}`); }}
        />
      )}
    </div>
  );
};

export default OrdersPage;
